import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LifeBuoy, X } from "lucide-react";
import type { SupportTicketDto } from "@finora/types";
import { Button, EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { apiFetch } from "../../lib/api";

const STATUS_LABELS: Record<string, string> = {
  OPEN: "Otvoren",
  IN_PROGRESS: "U obradi",
  RESOLVED: "Rešen",
  CLOSED: "Zatvoren",
};

const STATUS_TONE: Record<string, "success" | "error" | "neutral" | "warning"> = {
  OPEN: "warning",
  IN_PROGRESS: "neutral",
  RESOLVED: "success",
  CLOSED: "neutral",
};

const PRIORITY_LABELS: Record<string, string> = {
  LOW: "Nizak",
  MEDIUM: "Srednji",
  HIGH: "Visok",
};

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("sr-RS");
}

function NewTicketModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      apiFetch<SupportTicketDto>("/support-tickets", {
        method: "POST",
        body: JSON.stringify({ subject, message, priority }),
      }),
    onSuccess: (ticket) => onCreated(ticket.id),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-auto rounded-xl bg-surface shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Novi tiket podrške</h2>
          <button
            type="button"
            aria-label="Zatvori"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="flex flex-col gap-4 px-5 py-4">
          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700">Naslov</span>
            <input
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-lg border border-border px-3 py-2 text-sm"
              placeholder="Kratak opis problema"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700">Prioritet</span>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="rounded-lg border border-border px-3 py-2 text-sm"
            >
              <option value="LOW">Nizak</option>
              <option value="MEDIUM">Srednji</option>
              <option value="HIGH">Visok</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700">Opišite problem</span>
            <textarea
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="rounded-lg border border-border px-3 py-2 text-sm"
              placeholder="Što detaljnije opišite šta se dešava..."
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Otkaži
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Šaljem..." : "Pošalji"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function SupportTicketsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showNew, setShowNew] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["support-tickets"],
    queryFn: () => apiFetch<SupportTicketDto[]>("/support-tickets"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Podrška</h1>
          <p className="text-sm text-slate-500">
            Otvorite tiket ako imate problem ili pitanje - HAFiskal tim odgovara ovde.
          </p>
        </div>
        <Button onClick={() => setShowNew(true)}>Novi tiket</Button>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : isError ? (
          <div className="p-6 text-sm text-red-600">Greška pri učitavanju tiketa. Osvežite stranicu.</div>
        ) : !data || data.length === 0 ? (
          <EmptyState
            icon={LifeBuoy}
            title="Nemate otvorenih tiketa"
            description="Kliknite 'Novi tiket' ako vam treba pomoć."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Naslov</th>
                <th className="px-4 py-3">Prioritet</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Poslednja aktivnost</th>
              </tr>
            </thead>
            <tbody>
              {data.map((t) => (
                <tr
                  key={t.id}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-slate-50"
                  onClick={() => navigate(`/podrska/${t.id}`)}
                >
                  <td className="px-4 py-3 font-medium text-slate-800">{t.subject}</td>
                  <td className="px-4 py-3 text-slate-600">{PRIORITY_LABELS[t.priority] ?? t.priority}</td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      label={STATUS_LABELS[t.status] ?? t.status}
                      tone={STATUS_TONE[t.status] ?? "neutral"}
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-500">{formatDateTime(t.lastMessageAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNew && (
        <NewTicketModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
            navigate(`/podrska/${id}`);
          }}
        />
      )}
    </div>
  );
}
